import { sortBy } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { sortInternalExperiences } from "../helpers";
import { Position, ProfileExportFormatted } from "../types";

type Category = { id: number; text: string };
export type FullExperience = Position & {
  internalExperiences: ProfileExportFormatted["_originalData"]["internal_experiences"];
};

type CategoryWithExperience = [Category, FullExperience[]];

export const getPositionsByCategory = (
  positions: ProfileExportFormatted["positions"],
  internalExperiences: ProfileExportFormatted["_originalData"]["internal_experiences"],
  t: (text: string) => string
) => {
  const orderedInternalExperiences = sortInternalExperiences(internalExperiences || []);

  const positionsByCategory: CategoryWithExperience[] = positions.items.reduce(
    (acc: CategoryWithExperience[], curr: FullExperience) => {
      const category = curr._originalPosition.category?.[0] || { id: 0, text: t("otherCategory") };

      const filteredInternalExperiences = (orderedInternalExperiences || []).filter(
        ({ metadata }) => metadata.position_id === curr.id
      );

      const fullExp: FullExperience = {
        ...curr,
        internalExperiences: filteredInternalExperiences
      };
      const hasCategory = !!acc.find(([cat]) => cat.id === category.id);
      if (!hasCategory) {
        return [...acc, [category, [fullExp]]] as CategoryWithExperience[];
      }
      return acc.map(([cat, positions]) =>
        cat.id === category.id ? [cat, [...positions, fullExp]] : [cat, positions]
      ) as CategoryWithExperience[];
    },
    []
  );

  return positionsByCategory
    .sort((item) => (Number(item[0].id) === 0 ? 1 : -1))
    .map(([cat, exp]) => [
      cat,
      sortBy(exp, (pos: Position) => -Number(pos.main))
    ]) as CategoryWithExperience[];
};

export const usePositionsByCategory = (
  positions: ProfileExportFormatted["positions"],
  internalExperiences?: ProfileExportFormatted["_originalData"]["internal_experiences"]
) => {
  const { t } = useTranslation("profiles", { keyPrefix: "export" });

  const positionsByCategory = useMemo(
    () => getPositionsByCategory(positions, internalExperiences || [], t),
    [internalExperiences, positions, t]
  );

  return positionsByCategory;
};
